import { effect, Injectable } from "@angular/core";
import { RxStompService } from "./rx-stomp-service";
import { Observable, Subject, Subscription } from "rxjs";
import { SessionMessageBody } from "../models/session-message-body";
import { SessionService } from "./session-service";
import { Message } from '@stomp/stompjs'
import { UserService } from "./user-service";

@Injectable({
  providedIn: 'root',
})
export class SessionMessageService {
    private sessionSubscription!: Subscription;
    private technicalEligibilitySubscription!: Subscription;

    private readonly messageSubject = new Subject<SessionMessageBody>();
    private readonly siteUpdateSubject = new Subject<SessionMessageBody>();
    private readonly siteCreationSubject = new Subject<SessionMessageBody>();
    private readonly siteDeletionSubject = new Subject<SessionMessageBody>();
    private readonly sitesValidationSubject = new Subject<string>();
    private readonly sessionCreationSubject = new Subject<SessionMessageBody>();
    private readonly sessionDeletionSubject = new Subject<SessionMessageBody>();
    private readonly errorSubject = new Subject<SessionMessageBody>();

    private readonly technicalEligibilityBulkRequestCreationSubject = new Subject<any>();
    private readonly technicalEligibilityBulkRequestUpdateSubject = new Subject<any>();
    private readonly technicalEligibilitySiteProviderResponseCreateSubject = new Subject<any>();
    private readonly technicalEligibilitySiteRequestUpdateSubject = new Subject<any>();

    public readonly getAllNotifications$: Observable<SessionMessageBody> = this.messageSubject.asObservable();
    public readonly getSiteUpdateNotifications$: Observable<SessionMessageBody> = this.siteUpdateSubject.asObservable();
    public readonly getSiteCreationNotifications$: Observable<SessionMessageBody> = this.siteCreationSubject.asObservable();
    public readonly getSiteDeletionNotifications$: Observable<SessionMessageBody> = this.siteDeletionSubject.asObservable();
    public readonly getSitesValidationNotifications$: Observable<string> = this.sitesValidationSubject.asObservable();
    public readonly getSessionCreationNotifications$: Observable<SessionMessageBody> = this.sessionCreationSubject.asObservable();
    public readonly getSessionDeletionlNotifications$: Observable<SessionMessageBody> = this.sessionDeletionSubject.asObservable();
    public readonly getErrorNotifications$: Observable<SessionMessageBody> = this.errorSubject.asObservable();

    public readonly getTechnicalEligibilityBulkRequestCreationNotifications$: Observable<any> = this.technicalEligibilityBulkRequestCreationSubject.asObservable();
    public readonly getTechnicalEligibilityBulkRequestUpdateNotifications$: Observable<any> = this.technicalEligibilityBulkRequestUpdateSubject.asObservable();
    public readonly getTechnicalEligibilitySiteProviderResponseCreationNotifications$: Observable<any> = this.technicalEligibilitySiteProviderResponseCreateSubject.asObservable();
    public readonly getTechnicalEligibilitySiteRequestUpdateNotifications$: Observable<any> = this.technicalEligibilitySiteRequestUpdateSubject.asObservable();

    public connectionLost$!: Observable<boolean>;

    constructor(
        private readonly rxStompService: RxStompService,
        private readonly sessionService: SessionService,
        private readonly userService: UserService
    ) {        
        this.connectionLost$ = this.rxStompService.connectionLost$.asObservable();
        
        effect(() => {
            const currentSessionCode = this.sessionService.currentSessionCode();

            if (!currentSessionCode || currentSessionCode === '') {
                return;
            }

            if (this.sessionSubscription) {
                this.sessionSubscription.unsubscribe();
            }

            this.sessionSubscription = this.rxStompService.watch(`/exchange/sessions/${currentSessionCode}`).subscribe({
                next: (message: Message) => {
                    const sessionMessageBody: SessionMessageBody = JSON.parse(message.body);

                    if (!sessionMessageBody.username || (this.userService.username() !== sessionMessageBody.username)) {
                        // Message not caused by current user's actions
                        this.handleSessionMessage(sessionMessageBody);
                    }
                },
                error: (error: any) => {
                    this.messageSubject.error(error);
                }
            });
        });
    }

    listenToTechnicalEligibilityBulkRequestUpdates(requestId: number) {
        if (this.technicalEligibilitySubscription) {
            this.technicalEligibilitySubscription.unsubscribe();
        }

        this.technicalEligibilitySubscription = this.rxStompService.watch(`/exchange/technicalEligibilityBulkRequests/${requestId}`).subscribe({
            next: (message: Message) => {
                const messageBody: any = JSON.parse(message.body);
                this.handleTechnicalEligibilityBulkRequestMessage(messageBody);
            },
            error: (error: any) => {
                this.messageSubject.error(error);
            }
        })
    }

    handleSessionMessage(sessionMessageBody: SessionMessageBody): void {
        console.debug("handleSessionMessage", sessionMessageBody);

        this.messageSubject.next(sessionMessageBody);

        switch (sessionMessageBody.entityType) {
            case 'site':
                switch (sessionMessageBody.action) {
                    case 'update':
                        this.siteUpdateSubject.next(sessionMessageBody);
                        break;
                    case 'create':
                        this.siteCreationSubject.next(sessionMessageBody);
                        break;
                    case 'delete':
                        this.siteDeletionSubject.next(sessionMessageBody);
                        break;
                    case 'error':
                        this.errorSubject.next(sessionMessageBody);
                        break;
                    default:
                        console.log("WARNING: Invalid action");
                }
                break;
            case 'session':
                switch (sessionMessageBody.action) {
                    case 'create':
                        this.sessionCreationSubject.next(sessionMessageBody);
                        break;
                    case 'delete':
                        this.sessionDeletionSubject.next(sessionMessageBody);
                        break;
                    default:
                        console.log("WARNING: Invalid action");
                }
                break;
            default:
                console.log("WARNING: Invalid entityType");
        }
    }

    handleTechnicalEligibilityBulkRequestMessage(messageBody: any) {
        console.debug("handleTechnicalEligibilityBulkRequestMessage", messageBody);

        switch(messageBody.entityType) {
            case 'technicalEligibilityBulkRequest':
                switch (messageBody.action) {
                    case 'update':
                        this.technicalEligibilityBulkRequestUpdateSubject.next(messageBody);
                        break;
                    case 'create':
                        this.technicalEligibilityBulkRequestCreationSubject.next(messageBody);
                        break;
                    case 'delete':
                        break;
                    case 'error':
                        this.errorSubject.next(messageBody);
                        break;
                    default:
                        console.log("WARNING: Invalid action");
                }
                break;
            case 'technicalEligibilitySiteProviderResponse':
                switch (messageBody.action) {
                    case 'update':
                        break;
                    case 'create':
                        this.technicalEligibilitySiteProviderResponseCreateSubject.next(messageBody);
                        break;
                    case 'delete':
                        break;
                    case 'error':
                        this.errorSubject.next(messageBody);
                        break;
                    default:
                        console.log("WARNING: Invalid action");
                }
                break;
            case 'technicalEligibilitySiteRequest':
                switch (messageBody.action) {
                    case 'create':
                        break;
                    case 'update':
                        this.technicalEligibilitySiteRequestUpdateSubject.next(messageBody);
                        break;
                    case 'delete':
                        break;
                    case 'error':
                        this.errorSubject.next(messageBody);
                        break;
                    default:
                        console.log("WARNING: Invalid action");
                }
                break;
            default:
                console.log("WARNING: Invalid entityType");
        }
    }

    ngOnDestroy() {
        if (this.technicalEligibilitySubscription) {
            this.technicalEligibilitySubscription.unsubscribe();
        }

        if (this.sessionSubscription) {
            this.sessionSubscription.unsubscribe();
        }
    }


}